import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, ArrowLeft, Upload, Loader2, Check, Home, Building2, Building,
  Sparkles, FileCheck2, X, MapPin, Download, ShieldCheck, CalendarDays, TrendingDown, ClipboardList,
  ChevronDown, Plus, Trash2, Ruler, Star,
} from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { api, BUILDING_TYPES, REALIZATION, track, getVisitor } from "@/lib/api";
import GdprConsent from "@/components/GdprConsent";
import InfoTip from "@/components/InfoTip";

const eur = (n) =>
  new Intl.NumberFormat("sk-SK", { maximumFractionDigits: 0 }).format(Math.round(n || 0)) + " €";

const TYPE_ICONS = { family_house: Home, apartment_building: Building2, building: Building };

const TYPE_IMAGES = {
  family_house: "https://images.unsplash.com/photo-1766603636700-e9d80473f40f?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  apartment_building: "https://images.pexels.com/photos/28295552/pexels-photo-28295552.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=800",
  building: "https://images.unsplash.com/photo-1694702740570-0a31ee1525c7?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
};

const WALL_TYPES = [
  { key: "porobeton", name: "Pórobetónová tvárnica", desc: "Ytong, Porfix a pod. — bez príplatku." },
  { key: "drevostavba", name: "Drevostavba", desc: "Drevený nosný systém — kotvenie do dreva." },
  { key: "dierovana_tehla", name: "Dierovaná tehla", desc: "Porotherm a pod. — potrebné dlhšie kotvy." },
  { key: "beton", name: "Betón", desc: "Monolit / panel — najnáročnejšie kotvenie." },
];

const LOADER_MSGS = [
  "Počítam materiál zateplovacieho systému…",
  "Kalkulujem prácu montáže…",
  "Pripočítavam lešenie a dopravu…",
  "Overujem partnera vo vašom okrese…",
  "Pripravujem vašu cenovú ponuku…",
];

export default function Calculator({ presetDistrict = "" }) {
  const [pricing, setPricing] = useState(null);
  const [refs, setRefs] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    building_type: "family_house",
    area: "",
    thickness: 160,
    material: "eps_grafit",
    realization: "flexible",
    wall_type: "porobeton",
    district: presetDistrict || "",
    plaster_type: "silikonova",
  });
  const [live, setLive] = useState(null);
  const [preciseOn, setPreciseOn] = useState(false);
  const [precise, setPrecise] = useState({
    length_m: "", width_m: "", floors: 2, sokel_height_m: "",
    soffit_width_m: "", soffit_variant: "battens",
    windows: [{ width_m: "", height_m: "", count: 1, kind: "window", full_height: false }],
  });
  const [planFiles, setPlanFiles] = useState({ floor_plan: null, sections: null });
  const [planResult, setPlanResult] = useState(null);
  const [planAnalyzing, setPlanAnalyzing] = useState(false);
  const setPrec = (k, v) => setPrecise((p) => ({ ...p, [k]: v }));
  const setWindow = (i, k, v) =>
    setPrecise((p) => ({ ...p, windows: p.windows.map((w, idx) => (idx === i ? { ...w, [k]: v } : w)) }));
  const addWindow = () =>
    setPrecise((p) => ({ ...p, windows: [...p.windows, { width_m: "", height_m: "", count: 1, kind: "window", full_height: false }] }));
  const removeWindow = (i) =>
    setPrecise((p) => ({ ...p, windows: p.windows.filter((_, idx) => idx !== i) }));

  const buildPayload = () => {
    const base = { ...form, area: Number(form.area), thickness: Number(form.thickness), visitor_id: getVisitor().id, ai_used: !!planResult };
    if (preciseOn && Number(precise.length_m) > 0 && Number(precise.width_m) > 0) {
      base.precise = {
        length_m: Number(precise.length_m),
        width_m: Number(precise.width_m),
        floors: Number(precise.floors) || 1,
        sokel_height_m: Number(precise.sokel_height_m) || 0,
        soffit_width_m: Number(precise.soffit_width_m) || 0,
        soffit_variant: precise.soffit_variant,
        windows: precise.windows
          .filter((w) => Number(w.width_m) > 0 && Number(w.count) > 0)
          .map((w) => ({
            width_m: Number(w.width_m), height_m: Number(w.height_m) || 0, count: Number(w.count),
            kind: w.kind === "door" ? "door" : "window",
            full_height: w.kind === "door" ? true : !!w.full_height,
          })),
      };
    }
    return base;
  };

  const analyzePlans = async () => {
    if (!planFiles.floor_plan || !planFiles.sections) {
      toast.error("Nahrajte pôdorys aj rezy domu.");
      return;
    }
    setPlanAnalyzing(true);
    const fd = new FormData();
    fd.append("floor_plan", planFiles.floor_plan);
    fd.append("sections", planFiles.sections);
    try {
      const { data } = await api.post("/analyze-plans", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 180000,
      });
      setPlanResult(data);
      if (data.net_area_m2) set("area", Math.round(data.net_area_m2));
      setPreciseOn(true);
      setPrecise((p) => ({
        ...p,
        length_m: data.length_m ?? p.length_m,
        width_m: data.width_m ?? p.width_m,
        floors: data.floors ?? p.floors,
        sokel_height_m: p.sokel_height_m !== "" ? p.sokel_height_m : (pricing?.precise_public?.sokel_default_height_m ?? 0.6),
        soffit_width_m: data.soffit_width_m ?? p.soffit_width_m,
        windows: data.windows?.length
          ? data.windows.map((w) => ({ width_m: w.width_m, height_m: w.height_m, count: w.count, kind: w.kind, full_height: w.full_height }))
          : p.windows,
      }));
      toast.success("AI výkaz je pripravený — údaje sme doplnili do presného režimu.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI analýza zlyhala. Vyplňte údaje ručne.");
    } finally {
      setPlanAnalyzing(false);
    }
  };
  const [contact, setContact] = useState({ name: "", email: "", phone: "", city: "", note: "" });
  const [gdpr, setGdpr] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaderMsg, setLoaderMsg] = useState(0);
  const [carousel, setCarousel] = useState(0);
  const [final, setFinal] = useState(null);
  const [leadId, setLeadId] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  // Zachytenie e-mailu z výsledku kalkulačky — A/B: polovica návštev vidí jemný box, polovica mäkkú bránu (blur ponúk)
  const [captured, setCaptured] = useState(() => localStorage.getItem("z_calc_captured") === "1");
  const [gateVariant] = useState(() => {
    let h = 0;
    const s = getVisitor().id;
    for (const ch of s) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
    return h % 2 === 1;
  });

  const captureEmail = async (email, gdprOk) => {
    const { data } = await api.post("/calc-capture", { email, gdpr: gdprOk, calc_input: buildPayload(), variant: gateVariant ? "gate" : "box" });
    localStorage.setItem("z_calc_captured", "1");
    setCaptured(true);
    track("contact_calc_capture", gateVariant ? "gate" : "box");
    return data;
  };

  // A/B meranie: koľkí návštevníci capture box/gate videli s hotovým výsledkom (raz za session)
  useEffect(() => {
    if (!live || captured) return;
    if (sessionStorage.getItem("z_ab_shown")) return;
    sessionStorage.setItem("z_ab_shown", "1");
    track("calc_capture_shown", gateVariant ? "gate" : "box");
  }, [live, captured, gateVariant]);

  useEffect(() => {
    api.get("/pricing").then((r) => setPricing(r.data)).catch(() => {});
    api.get("/references").then((r) => setRefs(r.data)).catch(() => {});
    api.get("/districts").then((r) => setDistricts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (presetDistrict) setForm((f) => ({ ...f, district: presetDistrict }));
  }, [presetDistrict]);

  // live estimate (debounced)
  useEffect(() => {
    if (!form.area || Number(form.area) <= 0) { setLive(null); return; }
    const t = setTimeout(() => {
      api.post("/calculate", buildPayload())
        .then((r) => setLive(r.data))
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [form, preciseOn, precise]);

  // loader animation cycling
  useEffect(() => {
    if (!loading) return;
    const m = setInterval(() => setLoaderMsg((x) => (x + 1) % LOADER_MSGS.length), 1000);
    const c = setInterval(() => setCarousel((x) => (refs.length ? (x + 1) % refs.length : 0)), 900);
    return () => { clearInterval(m); clearInterval(c); };
  }, [loading, refs.length]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canNext = () => {
    if (step === 0) return !!form.building_type;
    if (step === 1) return Number(form.area) > 0;
    if (step === 3) return !!form.realization;
    return true;   // krok 2 (dokumenty) je dobrovoľný
  };

  const submit = useCallback(async () => {
    if (!contact.name || !contact.email) {
      toast.error("Vyplňte meno a e-mail.");
      return;
    }
    if (!gdpr) {
      toast.error("Potvrďte súhlas so spracovaním osobných údajov.");
      return;
    }
    setLoading(true);
    setLoaderMsg(0);
    const start = Date.now();
    try {
      const { data } = await api.post("/leads", {
        ...contact,
        calc_input: buildPayload(),
        project_note: planResult ? "AI výkaz z dokumentov (pôdorys + rezy)" : "",
        plan_files: planResult?.files || [],
      });
      const elapsed = Date.now() - start;
      await new Promise((res) => setTimeout(res, Math.max(0, 4200 - elapsed)));
      setFinal(data.result);
      setLeadId(data.id || "");
      setEmailSent(data.email_sent);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Odoslanie zlyhalo. Skúste to znova.");
    } finally {
      setLoading(false);
    }
  }, [contact, form, gdpr, preciseOn, precise, planResult]);

  const reset = () => {
    setFinal(null);
    setStep(0);
    setContact({ name: "", email: "", phone: "", city: "", note: "" });
    setGdpr(false);
    setPlanFiles({ floor_plan: null, sections: null });
    setPlanResult(null);
  };

  const materials = pricing?.materials || [];
  const thicknesses = pricing?.thicknesses || [80, 100, 120, 140, 160, 180, 200];

  return (
    <section id="kalkulacka" className="bg-ink-2 py-24 md:py-36 border-t border-white/10 relative">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="mb-14 max-w-3xl">
          <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-5">Kalkulačka zateplenia</p>
          <h2 className="font-display text-5xl md:text-7xl leading-[0.9] text-white">
            Cena zateplenia<br /><span className="text-gold">do 60 sekúnd</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start">
          {/* LEFT: steps */}
          <div className="lg:col-span-7 border border-white/10 bg-ink p-6 md:p-10 min-h-[520px] flex flex-col">
            {/* progress */}
            <div className="flex items-center gap-2 mb-10">
              {["Typ", "Rozmery", "Dokumenty", "Termín", "Kontakt"].map((s, i) => (
                <div key={s} className="flex-1">
                  <div className={`h-1 ${i <= step ? "bg-gold" : "bg-white/10"} transition-colors`} />
                  <span className={`mt-2 block font-mono2 text-[10px] uppercase tracking-widest ${i <= step ? "text-gold" : "text-white/40"}`}>
                    0{i + 1} {s}
                  </span>
                </div>
              ))}
            </div>

            <AnimatePresence mode="wait">
              {/* STEP 0 */}
              {step === 0 && (
                <motion.div key="s0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
                  <h3 className="font-display text-3xl md:text-4xl text-white mb-6">Aký objekt zatepľujete?</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {BUILDING_TYPES.map((b) => {
                      const Icon = TYPE_ICONS[b.key];
                      const active = form.building_type === b.key;
                      return (
                        <button
                          key={b.key}
                          onClick={() => set("building_type", b.key)}
                          data-testid={`btype-${b.key}`}
                          className={`group relative overflow-hidden text-left min-h-[220px] flex flex-col justify-end p-6 border transition-all ${active ? "border-gold" : "border-white/15 hover:border-white/40"}`}
                        >
                          {/* background image + gradient */}
                          <img
                            src={TYPE_IMAGES[b.key]}
                            alt={b.label}
                            loading="lazy"
                            className={`absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${active ? "opacity-100" : "opacity-70"}`}
                          />
                          <div className={`absolute inset-0 transition-opacity ${active ? "bg-gradient-to-t from-ink via-ink/80 to-gold/20" : "bg-gradient-to-t from-ink via-ink/85 to-ink/30"}`} />
                          {/* content */}
                          <div className="relative z-10">
                            <Icon className={`w-8 h-8 mb-3 ${active ? "text-gold" : "text-white/80"}`} />
                            <div className="font-bold text-white mb-1">{b.label}</div>
                            <div className="text-white/60 text-sm">{b.desc}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* STEP 1 */}
              {step === 1 && (
                <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }} className="space-y-8">
                  <h3 className="font-display text-3xl md:text-4xl text-white">Rozmery a materiál</h3>

                  {/* district / okres */}
                  <div>
                    <label className="font-mono2 text-xs uppercase tracking-widest text-white/60 mb-2 block">Okres realizácie</label>
                    <div className="relative">
                      <MapPin className="w-4 h-4 text-gold absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <select
                        value={form.district}
                        onChange={(e) => set("district", e.target.value)}
                        data-testid="calc-district-select"
                        className="w-full bg-ink border border-white/20 focus:border-gold focus:ring-1 focus:ring-gold outline-none text-white pl-11 pr-4 py-3 appearance-none"
                      >
                        <option value="">Vyberte okres…</option>
                        {districts.map((d) => (
                          <option key={d.name} value={d.name}>
                            {d.name}{d.has_partner ? " ✓ partner" : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* area */}
                  <div>
                    <label className="font-mono2 text-xs uppercase tracking-widest text-white/60 mb-2 block">Plocha fasády (m²)</label>
                    <input
                      type="number"
                      inputMode="numeric"
                      value={form.area}
                      onChange={(e) => set("area", e.target.value)}
                      placeholder="napr. 220"
                      data-testid="calc-area-input"
                      className="w-full bg-transparent border border-white/20 focus:border-gold focus:ring-1 focus:ring-gold outline-none text-white text-2xl font-mono2 px-4 py-3 placeholder-white/30"
                    />
                  </div>

                  {/* thickness */}
                  <div>
                    <label className="font-mono2 text-xs uppercase tracking-widest text-white/60 mb-3 block">
                      Hrúbka zateplenia (mm)<InfoTip topic="thickness" testid="info-tip-thickness" />
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {thicknesses.map((t) => (
                        <button
                          key={t}
                          onClick={() => set("thickness", t)}
                          data-testid={`thickness-${t}`}
                          className={`px-4 py-2 font-mono2 text-sm border transition-all ${Number(form.thickness) === t ? "border-gold bg-gold text-ink" : "border-white/20 text-white/70 hover:border-white/50"}`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* material */}
                  <div>
                    <label className="font-mono2 text-xs uppercase tracking-widest text-white/60 mb-3 block">
                      Materiál zateplenia<InfoTip topic="insulation" testid="info-tip-insulation" />
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {materials.map((m) => {
                        const active = form.material === m.key;
                        return (
                          <button
                            key={m.key}
                            onClick={() => set("material", m.key)}
                            data-testid={`material-${m.key}`}
                            className={`text-left p-4 border transition-all ${active ? "border-gold bg-gold/10" : "border-white/15 hover:border-white/40"}`}
                          >
                            <div className="text-white font-medium text-sm mb-1">{m.name}</div>
                            <div className="text-white/45 text-xs leading-snug">{m.desc}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* plaster (omietka) */}
                  <div>
                    <label className="font-mono2 text-xs uppercase tracking-widest text-white/60 mb-3 block">
                      Typ omietky<InfoTip topic="plaster" testid="info-tip-plaster" />
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {(pricing?.plaster_variants || []).map((v) => {
                        const active = form.plaster_type === v.key;
                        return (
                          <button
                            key={v.key}
                            onClick={() => set("plaster_type", v.key)}
                            data-testid={`plaster-${v.key}`}
                            className={`text-left p-4 border transition-all ${active ? "border-gold bg-gold/10" : "border-white/15 hover:border-white/40"}`}
                          >
                            <div className="text-white font-medium text-sm">{v.name}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* wall type (obvodové murivo) */}
                  <div>
                    <label className="font-mono2 text-xs uppercase tracking-widest text-white/60 mb-3 block">Aké je obvodové murivo?</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {WALL_TYPES.map((w) => {
                        const active = form.wall_type === w.key;
                        return (
                          <button
                            key={w.key}
                            onClick={() => set("wall_type", w.key)}
                            data-testid={`wall-${w.key}`}
                            className={`text-left p-4 border transition-all ${active ? "border-gold bg-gold/10" : "border-white/15 hover:border-white/40"}`}
                          >
                            <div className="text-white font-medium text-sm mb-1">{w.name}</div>
                            <div className="text-white/45 text-xs leading-snug">{w.desc}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* PRECISE MODE — výkaz výmery */}
                  <div className={`border transition-colors ${preciseOn ? "border-gold/40 bg-gold/[0.03]" : "border-white/15"}`}>
                    <button
                      type="button"
                      onClick={() => setPreciseOn((v) => !v)}
                      data-testid="precise-toggle"
                      className="w-full flex items-center justify-between gap-3 p-4 text-left"
                    >
                      <span className="flex items-center gap-3">
                        <Ruler className={`w-5 h-5 shrink-0 ${preciseOn ? "text-gold" : "text-white/50"}`} />
                        <span>
                          <span className={`block font-medium text-sm ${preciseOn ? "text-gold" : "text-white"}`}>Presný režim — výkaz výmery</span>
                          <span className="block text-white/45 text-xs mt-0.5">Pôdorys, okná, sokel → lišty, parapety, lešenie a odpad presne na váš dom</span>
                        </span>
                      </span>
                      <ChevronDown className={`w-5 h-5 shrink-0 transition-transform ${preciseOn ? "rotate-180 text-gold" : "text-white/40"}`} />
                    </button>

                    {preciseOn && (
                      <div className="px-4 pb-5 space-y-6 border-t border-white/10 pt-5" data-testid="precise-panel">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <div>
                            <label className="font-mono2 text-[10px] uppercase tracking-widest text-white/50 mb-1.5 block">Dĺžka domu (m)</label>
                            <input type="number" min="0" step="0.1" inputMode="decimal" value={precise.length_m} onChange={(e) => setPrec("length_m", e.target.value)} placeholder="12" data-testid="precise-length"
                              className="w-full bg-transparent border border-white/20 focus:border-gold outline-none text-white font-mono2 px-3 py-2.5 placeholder-white/25" />
                          </div>
                          <div>
                            <label className="font-mono2 text-[10px] uppercase tracking-widest text-white/50 mb-1.5 block">Šírka domu (m)</label>
                            <input type="number" min="0" step="0.1" inputMode="decimal" value={precise.width_m} onChange={(e) => setPrec("width_m", e.target.value)} placeholder="9" data-testid="precise-width"
                              className="w-full bg-transparent border border-white/20 focus:border-gold outline-none text-white font-mono2 px-3 py-2.5 placeholder-white/25" />
                          </div>
                          <div>
                            <label className="font-mono2 text-[10px] uppercase tracking-widest text-white/50 mb-1.5 block">Počet podlaží</label>
                            <input type="number" min="1" max="10" step="1" inputMode="numeric" value={precise.floors} onChange={(e) => setPrec("floors", e.target.value)} data-testid="precise-floors"
                              className="w-full bg-transparent border border-white/20 focus:border-gold outline-none text-white font-mono2 px-3 py-2.5" />
                          </div>
                          <div>
                            <label className="font-mono2 text-[10px] uppercase tracking-widest text-white/50 mb-1.5 block">Výška soklu (m)</label>
                            <input type="number" min="0" step="0.1" inputMode="decimal" value={precise.sokel_height_m} onChange={(e) => setPrec("sokel_height_m", e.target.value)} placeholder="0 = bez soklu" data-testid="precise-sokel-height"
                              className="w-full bg-transparent border border-white/20 focus:border-gold outline-none text-white font-mono2 px-3 py-2.5 placeholder-white/25" />
                          </div>
                        </div>

                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="font-mono2 text-[10px] uppercase tracking-widest text-white/50">Okná — výpočet parapetov</label>
                            <button type="button" onClick={addWindow} data-testid="precise-window-add"
                              className="flex items-center gap-1.5 text-xs text-gold border border-gold/40 px-3 py-1.5 hover:bg-gold hover:text-ink transition-colors">
                              <Plus className="w-3.5 h-3.5" /> Pridať riadok
                            </button>
                          </div>
                          <div className="space-y-2">
                            {precise.windows.map((w, i) => (
                              <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`precise-window-row-${i}`}>
                                <select value={w.kind || "window"} onChange={(e) => setWindow(i, "kind", e.target.value)} data-testid={`precise-window-kind-${i}`}
                                  className="bg-ink border border-white/20 focus:border-gold outline-none text-white px-2 py-2 text-sm appearance-none">
                                  <option value="window">Okno</option>
                                  <option value="door">Dvere</option>
                                </select>
                                <input type="number" min="0" step="0.1" inputMode="decimal" value={w.width_m} onChange={(e) => setWindow(i, "width_m", e.target.value)} placeholder="šírka (m)" data-testid={`precise-window-width-${i}`}
                                  className="w-24 bg-transparent border border-white/20 focus:border-gold outline-none text-white font-mono2 px-3 py-2 text-sm placeholder-white/25" />
                                <span className="text-white/40 text-xs">×</span>
                                <input type="number" min="0" step="0.1" inputMode="decimal" value={w.height_m} onChange={(e) => setWindow(i, "height_m", e.target.value)} placeholder="výška (m)" data-testid={`precise-window-height-${i}`}
                                  className="w-24 bg-transparent border border-white/20 focus:border-gold outline-none text-white font-mono2 px-3 py-2 text-sm placeholder-white/25" />
                                <span className="text-white/40 text-xs">×</span>
                                <input type="number" min="1" step="1" inputMode="numeric" value={w.count} onChange={(e) => setWindow(i, "count", e.target.value)} data-testid={`precise-window-count-${i}`}
                                  className="w-14 bg-transparent border border-white/20 focus:border-gold outline-none text-white font-mono2 px-3 py-2 text-sm" />
                                <span className="text-white/40 text-xs">ks</span>
                                {w.kind !== "door" && (
                                  <label className="flex items-center gap-2 text-xs text-white/60 cursor-pointer select-none ml-1">
                                    <input type="checkbox" checked={!!w.full_height} onChange={(e) => setWindow(i, "full_height", e.target.checked)} data-testid={`precise-window-full-${i}`}
                                      className="accent-[#D4AF37] w-4 h-4" />
                                    od zeme
                                  </label>
                                )}
                                {precise.windows.length > 1 && (
                                  <button type="button" onClick={() => removeWindow(i)} data-testid={`precise-window-remove-${i}`} className="ml-auto text-white/30 hover:text-red-400 transition-colors">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                          <p className="text-white/35 text-[11px] mt-3 leading-relaxed">
                            Hĺbka parapetu = hrúbka izolácie + 6 cm → pri {form.thickness} mm izolácii je parapet {Number(form.thickness) + 60} mm.
                            Okná od zeme a dvere parapet nemajú — počítajú sa s 0 bm. Obvody otvorov použijeme na špalety a lišty.
                          </p>
                        </div>

                        {/* podhľad */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="font-mono2 text-[10px] uppercase tracking-widest text-white/50">
                              Podhľad (presah strechy)<InfoTip topic="soffit" testid="info-tip-soffit" />
                            </label>
                          </div>
                          <div className="flex items-center gap-3 mb-3">
                            <input type="number" min="0" step="0.05" inputMode="decimal" value={precise.soffit_width_m} onChange={(e) => setPrec("soffit_width_m", e.target.value)} placeholder="šírka podhľadu (m), 0 = bez" data-testid="precise-soffit-width"
                              className="w-48 bg-transparent border border-white/20 focus:border-gold outline-none text-white font-mono2 px-3 py-2 text-sm placeholder-white/25" />
                            <span className="text-white/35 text-[11px]">+ čielko 20 cm sa pripočíta automaticky</span>
                          </div>
                          {Number(precise.soffit_width_m) > 0 && (
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="soffit-variants">
                              {[
                                ["battens", "Laty po 25 cm", "najlacnejšie, stačí rovný krov", pricing?.precise_public?.soffit_battens_per_m2],
                                ["osb", "Plné debnenie OSB", "najpevnejšie a najčistejšie", pricing?.precise_public?.soffit_osb_per_m2],
                                ["koniky", "Koníky", "vyrovná nerovnosti, netreba rovnať väzníky", pricing?.precise_public?.soffit_koniky_per_m2],
                              ].map(([k, label, desc, price]) => {
                                const active = precise.soffit_variant === k;
                                return (
                                  <button key={k} type="button" onClick={() => setPrec("soffit_variant", k)} data-testid={`soffit-variant-${k}`}
                                    className={`text-left p-3 border transition-all ${active ? "border-gold bg-gold/10" : "border-white/15 hover:border-white/40"}`}>
                                    <div className="text-white text-sm font-medium">{label}</div>
                                    <div className="text-white/45 text-[11px] leading-snug mt-0.5">{desc}</div>
                                    {price != null && <div className="text-gold text-xs font-mono2 mt-1.5">{price} €/m²</div>}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        <p className="text-white/30 text-[11px] leading-relaxed border-t border-white/10 pt-3">
                          Z pôdorysu a podlaží dopočítame obvod domu (štartovacia lišta), výšku fasády a plochu lešenia
                          (rozpad na montáž, prenájom a demontáž). K materiálu pripočítame odpad na rezanie.
                        </p>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* STEP 2 — AI dokumenty */}
              {step === 2 && (
                <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }} className="space-y-6">
                  <div>
                    <h3 className="font-display text-3xl md:text-4xl text-white">Presná ponuka z dokumentov</h3>
                    <p className="text-white/50 text-sm mt-2 leading-relaxed">
                      Nahrajte pôdorys a rezy domu — naša AI z nich vypočíta plochu fasády, obvod na zakladaciu lištu,
                      okná a dvere, špalety, parapety, sokel aj podhľad. Čistejšie čísla = presnejšia cena.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      ["floor_plan", "Nahrajte pôdorys rodinného domu", "plan-floor-btn"],
                      ["sections", "Nahrajte rezy domu", "plan-sections-btn"],
                    ].map(([kind, label, tid]) => (
                      <label key={kind} data-testid={tid}
                        className={`border border-dashed p-5 flex items-center gap-4 cursor-pointer transition-colors ${planFiles[kind] ? "border-gold bg-gold/5" : "border-white/30 hover:border-gold hover:bg-gold/5"}`}>
                        <input type="file" accept=".pdf,image/*" className="hidden" data-testid={`${tid}-input`}
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) { if (f.size > 15 * 1024 * 1024) { toast.error("Súbor je príliš veľký (max 15 MB)."); return; } setPlanFiles((s) => ({ ...s, [kind]: f })); setPlanResult(null); } }} />
                        {planFiles[kind] ? <FileCheck2 className="w-6 h-6 text-gold shrink-0" /> : <Upload className="w-6 h-6 text-gold shrink-0" />}
                        <span>
                          <span className="block text-white font-medium text-sm">{planFiles[kind] ? planFiles[kind].name : label}</span>
                          <span className="block text-white/45 text-xs mt-0.5">{planFiles[kind] ? "Nahrané — môžete vymeniť" : "PDF alebo foto · max 15 MB"}</span>
                        </span>
                      </label>
                    ))}
                  </div>

                  <button
                    onClick={analyzePlans}
                    disabled={planAnalyzing || !planFiles.floor_plan || !planFiles.sections}
                    data-testid="plan-analyze-btn"
                    className={`w-full flex items-center justify-center gap-3 py-4 font-bold uppercase tracking-wider transition-colors ${planAnalyzing || !planFiles.floor_plan || !planFiles.sections ? "border border-white/15 text-white/30 cursor-not-allowed" : "bg-gold text-ink hover:bg-gold-light gold-glow"}`}
                  >
                    {planAnalyzing ? <><Loader2 className="w-5 h-5 animate-spin" /> AI analyzuje dokumenty…</> : <><Sparkles className="w-5 h-5" /> Vypočítať AI výkaz</>}
                  </button>

                  {planResult && (
                    <div className="border border-gold/40 bg-gold/5 p-5 space-y-3" data-testid="plan-result">
                      <div className="flex items-center gap-2 text-gold font-semibold text-sm">
                        <Check className="w-5 h-5" /> AI výkaz pripravený — údaje sú doplnené v presnom režime (krok Rozmery)
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                        {[
                          ["Čistá plocha", planResult.net_area_m2 ? `${planResult.net_area_m2} m²` : "—"],
                          ["Obvod domu", planResult.perimeter_m ? `${planResult.perimeter_m} bm` : "—"],
                          ["Otvory", planResult.openings_area_m2 ? `${planResult.openings_area_m2} m²` : "—"],
                          ["Parapety", planResult.sills_bm ? `${planResult.sills_bm} bm` : "—"],
                        ].map(([l, v]) => (
                          <div key={l} className="border border-white/10 p-2.5">
                            <div className="text-gold font-mono2 text-sm" data-testid={`plan-stat-${l.toLowerCase().replace(/[^a-z]/g, "")}`}>{v}</div>
                            <div className="text-white/45 text-[10px] uppercase tracking-widest font-mono2 mt-0.5">{l}</div>
                          </div>
                        ))}
                      </div>
                      {planResult.note && <p className="text-white/50 text-xs">AI poznámka: {planResult.note} (istota: {planResult.confidence})</p>}
                      {planResult.missing?.length > 0 && (
                        <div className="border-t border-white/10 pt-3" data-testid="plan-missing">
                          <p className="text-gold/90 text-xs font-semibold mb-2">Toto sa nepodarilo prečítať — doplňte prosím:</p>
                          <div className="flex flex-wrap gap-3">
                            {planResult.missing.some((m) => m.includes("pôdorysu")) && (
                              <>
                                <input type="number" min="0" step="0.1" placeholder="dĺžka (m)" value={precise.length_m} onChange={(e) => setPrec("length_m", e.target.value)} data-testid="missing-length"
                                  className="w-28 bg-transparent border border-gold/40 focus:border-gold outline-none text-white font-mono2 px-3 py-2 text-sm" />
                                <input type="number" min="0" step="0.1" placeholder="šírka (m)" value={precise.width_m} onChange={(e) => setPrec("width_m", e.target.value)} data-testid="missing-width"
                                  className="w-28 bg-transparent border border-gold/40 focus:border-gold outline-none text-white font-mono2 px-3 py-2 text-sm" />
                              </>
                            )}
                            {planResult.missing.some((m) => m.includes("podlažia")) && (
                              <input type="number" min="1" step="1" placeholder="počet podlaží" value={precise.floors} onChange={(e) => setPrec("floors", e.target.value)} data-testid="missing-floors"
                                className="w-32 bg-transparent border border-gold/40 focus:border-gold outline-none text-white font-mono2 px-3 py-2 text-sm" />
                            )}
                            {planResult.missing.some((m) => m.includes("podhľadu")) && (
                              <input type="number" min="0" step="0.05" placeholder="šírka podhľadu (m)" value={precise.soffit_width_m} onChange={(e) => setPrec("soffit_width_m", e.target.value)} data-testid="missing-soffit"
                                className="w-40 bg-transparent border border-gold/40 focus:border-gold outline-none text-white font-mono2 px-3 py-2 text-sm" />
                            )}
                            {planResult.missing.some((m) => m.includes("okná")) && (
                              <span className="text-white/60 text-xs self-center">Okná a dvere doplňte v presnom režime (krok Rozmery).</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <p className="text-white/35 text-xs leading-relaxed border-t border-white/10 pt-4">
                    Nemáte projekt po ruke? Pokojne pokračujte ďalej — stačí plocha fasády z predošlého kroku.
                    Presnosť údajov z dokumentov vždy potvrdíme pri bezplatnej obhliadke.
                  </p>
                </motion.div>
              )}

              {/* STEP 3 — Termín */}
              {step === 3 && (
                <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
                  <h3 className="font-display text-3xl md:text-4xl text-white mb-6">Kedy chcete realizovať?</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {REALIZATION.map((r) => {
                      const active = form.realization === r.key;
                      return (
                        <button
                          key={r.key}
                          onClick={() => set("realization", r.key)}
                          data-testid={`realization-${r.key}`}
                          className={`text-left p-6 border transition-all ${active ? "border-gold bg-gold/10" : "border-white/15 hover:border-white/40"}`}
                        >
                          <div className="font-bold text-white mb-1">{r.label}</div>
                          <div className="text-white/50 text-sm">{r.desc}</div>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* STEP 4 — Kontakt */}
              {step === 4 && (
                <motion.div key="s4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }} className="space-y-4">
                  <h3 className="font-display text-3xl md:text-4xl text-white mb-2">Kam pošleme ponuku?</h3>
                  <p className="text-white/50 text-sm mb-4">Cenový odhad vám pošleme aj na e-mail.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Meno a priezvisko *" testid="lead-name" value={contact.name} onChange={(v) => setContact({ ...contact, name: v })} />
                    <Field label="E-mail *" testid="lead-email" type="email" value={contact.email} onChange={(v) => setContact({ ...contact, email: v })} />
                    <Field label="Telefón" testid="lead-phone" value={contact.phone} onChange={(v) => setContact({ ...contact, phone: v })} />
                    <Field label="Mesto / okres" testid="lead-city" value={contact.city} onChange={(v) => setContact({ ...contact, city: v })} />
                  </div>
                  <div>
                    <label className="font-mono2 text-xs uppercase tracking-widest text-white/60 mb-2 block">Poznámka</label>
                    <textarea
                      value={contact.note}
                      onChange={(e) => setContact({ ...contact, note: e.target.value })}
                      data-testid="lead-note"
                      rows={3}
                      className="w-full bg-transparent border border-white/20 focus:border-gold focus:ring-1 focus:ring-gold outline-none text-white px-4 py-3 placeholder-white/30 resize-none"
                      placeholder="Napr. rok stavby, stav fasády, poschodia…"
                    />
                  </div>
                  <GdprConsent checked={gdpr} onChange={setGdpr} testid="lead-gdpr" className="pt-1" />
                </motion.div>
              )}
            </AnimatePresence>

            {/* nav */}
            <div className="mt-auto pt-10 flex items-center justify-between gap-4">
              <button
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                data-testid="calc-prev"
                className={`flex items-center gap-2 px-5 py-3 border border-white/20 text-sm uppercase tracking-wider transition-colors ${step === 0 ? "opacity-30 cursor-not-allowed" : "hover:border-white/50 text-white"}`}
              >
                <ArrowLeft className="w-4 h-4" /> Späť
              </button>
              {step < 4 ? (
                <button
                  onClick={() => canNext() ? setStep((s) => s + 1) : toast.error("Vyplňte údaje pre pokračovanie.")}
                  data-testid="calc-next"
                  className="group flex items-center gap-2 bg-gold text-ink px-8 py-3 font-bold uppercase tracking-wider hover:bg-gold-light transition-colors"
                >
                  Pokračovať <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              ) : (
                <button
                  onClick={submit}
                  data-testid="calc-submit-button"
                  className="group flex items-center gap-2 bg-gold text-ink px-8 py-3 font-bold uppercase tracking-wider hover:bg-gold-light transition-colors gold-glow"
                >
                  Získať presnú cenu <Sparkles className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* RIGHT: live result */}
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <ResultPanel live={live} form={form} captured={captured} gate={gateVariant} onCapture={captureEmail} />
          </div>
        </div>
      </div>

      {/* LOADER OVERLAY */}
      <AnimatePresence>
        {loading && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="calc-loader"
            className="fixed inset-0 z-[200] bg-ink/95 backdrop-blur-md flex flex-col items-center justify-center px-6"
          >
            <div className="relative w-24 h-24 mb-8">
              <div className="absolute inset-0 border-2 border-white/10 rounded-full" />
              <div className="absolute inset-0 border-2 border-t-gold border-transparent rounded-full animate-spin-slow" />
              <div className="absolute inset-0 flex items-center justify-center font-display text-gold text-2xl">60s</div>
            </div>
            <motion.p key={loaderMsg} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="text-white/80 font-mono2 text-sm uppercase tracking-widest mb-10 text-center">
              {LOADER_MSGS[loaderMsg]}
            </motion.p>
            {refs.length > 0 && (
              <div className="w-full max-w-md">
                <p className="text-white/40 text-xs uppercase tracking-widest font-mono2 mb-3 text-center">Naše referencie</p>
                <div className="relative aspect-[16/9] overflow-hidden border border-white/10">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={carousel}
                      src={refs[carousel]?.image}
                      initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                      className="w-full h-full object-cover"
                      alt="referencia"
                    />
                  </AnimatePresence>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-ink to-transparent p-4">
                    <span className="text-white text-sm font-bold">{refs[carousel]?.title} · {refs[carousel]?.location}</span>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* RESULT MODAL */}
      <AnimatePresence>
        {final && <FinalModal result={final} emailSent={emailSent} contact={contact} leadId={leadId} onClose={reset} buildingType={form.building_type} />}
      </AnimatePresence>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="font-mono2 text-xs uppercase tracking-widest text-white/60 mb-2 block">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-transparent border border-white/20 focus:border-gold focus:ring-1 focus:ring-gold outline-none text-white px-4 py-3 placeholder-white/30"
      />
    </div>
  );
}

function Row({ label, value, testid, accent }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-white/10">
      <span className="text-white/60 text-sm">{label}</span>
      <span data-testid={testid} className={`font-mono2 ${accent ? "text-gold" : "text-white"}`}>{value}</span>
    </div>
  );
}

function SubRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/5">
      <span className="text-white/45 text-xs">{label}</span>
      <span className="font-mono2 text-white/70 text-xs">{value}</span>
    </div>
  );
}

// Market price bands (SR 2026, ETICS s prácou, bez DPH, 10 cm baseline) — zdroj: stavebne-dopyty.sk
const MARKET_BANDS = { eps: [28, 40], eps_grafit: [32, 45], mineral: [40, 55] };

const SOFFIT_LABELS = { battens: "laty po 25 cm", osb: "plné debnenie OSB", koniky: "koníky" };

function MarketCompare({ result }) {
  const band = MARKET_BANDS[result.material_key];
  if (!band || !result.area) return null;
  // Adjust market band for thickness above the 10 cm baseline (+3–6 €/m² per 2 cm)
  const steps = Math.max(0, Math.round((result.thickness / 10 - 10) / 2));
  const lo = band[0] + 3 * steps;
  const hi = band[1] + 6 * steps;
  // Like-for-like: material + work + kotvenie per m², bez DPH (bez lešenia a dopravy)
  const b = result.breakdown;
  const ours = (b.material + b.labor + (b.anchoring || 0)) / result.area;
  const pct = Math.max(4, Math.min(96, ((ours - lo) / (hi - lo)) * 100));
  const status = ours <= lo
    ? { text: "Pod trhovým priemerom", cls: "text-emerald-400" }
    : ours <= hi
      ? { text: "V rámci trhového priemeru", cls: "text-gold" }
      : { text: "Zahŕňa kompletný systém, lešenie a dopravu", cls: "text-white/70" };
  return (
    <div className="mt-6 p-5 border border-white/10 bg-white/[0.02]" data-testid="market-compare">
      <div className="flex items-center justify-between mb-1">
        <span className="text-white/50 text-xs font-mono2 uppercase tracking-widest">Porovnanie s trhom</span>
        <span className={`text-xs font-semibold ${status.cls}`} data-testid="market-compare-status">{status.text}</span>
      </div>
      <p className="text-white/40 text-xs mb-4">
        Materiál + práca za m² (bez DPH) vs. trhový priemer zateplenia fasády na Slovensku.
      </p>
      {/* range bar */}
      <div className="relative h-2 bg-white/10 rounded-full">
        <div className="absolute inset-y-0 left-0 right-0 bg-gradient-to-r from-emerald-500/40 via-gold/40 to-red-500/30 rounded-full" />
        <div className="absolute -top-1 w-4 h-4 rounded-full bg-gold border-2 border-ink shadow-lg" style={{ left: `calc(${pct}% - 8px)` }} data-testid="market-compare-marker" />
      </div>
      <div className="flex items-center justify-between mt-3 text-xs">
        <span className="text-white/50">Trh: <span className="text-white/80 font-mono2">{Math.round(lo)}–{Math.round(hi)} €/m²</span></span>
        <span className="text-white/50">Vaša cena: <span className="text-gold font-mono2">≈ {Math.round(ours)} €/m²</span></span>
      </div>
      <p className="text-white/30 text-[11px] mt-4 leading-relaxed">
        Trhové ceny (ETICS s prácou, {result.thickness} mm, bez DPH) — zdroj:{" "}
        <a href="https://stavebne-dopyty.sk/zateplenie-domu-cena" target="_blank" rel="noreferrer" className="text-gold hover:underline">Stavebné-dopyty.sk</a>.
        Vaša cena navyše zahŕňa lešenie, dopravu a záruku 36/60 mesiacov.
      </p>
    </div>
  );
}

function SavingsEstimate({ area }) {
  if (!area) return null;
  // Orientačný odhad: typické náklady na kúrenie ~8 €/m² fasády/rok, úspora 30–40 %.
  const r10 = (x) => Math.round(x / 10) * 10;
  const lo = r10(area * 8 * 0.30);
  const hi = r10(area * 8 * 0.40);
  return (
    <div className="mt-4 p-5 border border-emerald-500/25 bg-emerald-500/[0.06]" data-testid="savings-estimate">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0">
          <TrendingDown className="w-5 h-5 text-emerald-400" />
        </div>
        <div>
          <div className="text-white/50 text-xs font-mono2 uppercase tracking-widest">Odhad úspory na kúrení</div>
          <div className="font-display text-3xl md:text-4xl text-emerald-400 leading-none mt-1" data-testid="savings-estimate-value">
            ≈ {lo}–{hi} €/rok
          </div>
        </div>
      </div>
      <p className="text-white/35 text-[11px] mt-4 leading-relaxed">
        Orientačný odhad pri úspore 30 – 40 % nákladov na vykurovanie domu tejto veľkosti. Skutočná úspora
        závisí od pôvodného stavu budovy, hrúbky izolácie a spôsobu vykurovania. Zateplenie zároveň zvyšuje
        komfort bývania a hodnotu nehnuteľnosti.
      </p>
    </div>
  );
}


function EmailCapture({ onCapture, testid = "calc-capture" }) {
  const [email, setEmail] = useState("");
  const [gdprOk, setGdprOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) {
      toast.error("Zadajte platnú e-mailovú adresu.");
      return;
    }
    if (!gdprOk) {
      toast.error("Potvrďte súhlas so spracovaním osobných údajov.");
      return;
    }
    setBusy(true);
    try {
      await onCapture(email.trim(), gdprOk);
      setDone(true);
      toast.success("Výpočet je na ceste do vašej schránky.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Odoslanie zlyhalo. Skúste to znova.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="border border-gold/30 bg-gold/5 p-3 flex items-center gap-2" data-testid={`${testid}-success`}>
        <Check className="w-4 h-4 text-gold shrink-0" />
        <p className="text-white/70 text-xs">Hotovo — skontrolujte si e-mail (aj priečinok Spam).</p>
      </div>
    );
  }
  return (
    <div className="border border-gold/40 bg-gold/5 p-4" data-testid={testid}>
      <p className="text-gold font-mono2 text-xs uppercase tracking-widest mb-1">Pošlite si tento výpočet e-mailom</p>
      <p className="text-white/60 text-xs mb-3">Detailná ponuka s výkazom + link na bezplatnú AI vizualizáciu vašej fasády.</p>
      <div className="flex gap-2 mb-3">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vas@email.sk"
          data-testid={`${testid}-email`}
          className="flex-1 min-w-0 bg-transparent border border-white/20 px-3 py-2 text-white text-sm placeholder:text-white/30 focus:border-gold focus:outline-none" />
        <button onClick={submit} disabled={busy} data-testid={`${testid}-submit`}
          className="shrink-0 bg-gold text-ink px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-gold-light transition-colors disabled:opacity-50 flex items-center gap-1.5">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />} Poslať
        </button>
      </div>
      <GdprConsent checked={gdprOk} onChange={setGdprOk} testid={`${testid}-gdpr`} />
    </div>
  );
}

function ResultPanel({ live, form, captured, gate, onCapture }) {
  const rangeEur = (total) => {
    const low = Math.round((total * 0.92) / 100) * 100;
    const high = Math.round((total * 1.08) / 100) * 100;
    const f = new Intl.NumberFormat("sk-SK", { maximumFractionDigits: 0 });
    return `${f.format(low)} – ${f.format(high)} €`;
  };
  return (
    <div className="border border-white/10 bg-ink p-6 md:p-8">
      <p className="font-mono2 text-xs uppercase tracking-widest text-gold mb-6">Orientačný odhad ceny</p>
      {!live ? (
        <div className="py-16 text-center text-white/40 text-sm">
          Zadajte plochu fasády a uvidíte<br />okamžité orientačné rozpätie ceny.
        </div>
      ) : (
        <>
          <div className="mb-6">
            <div className="text-white/40 text-xs font-mono2 uppercase tracking-widest mb-1">Orientačné rozpätie s DPH</div>
            <div data-testid="live-range" className="font-display text-4xl md:text-5xl text-gold leading-tight">{rangeEur(live.total)}</div>
            <div className="text-white/40 text-sm mt-2 font-mono2">plocha {live.area} m² · hrúbka {form.thickness} mm</div>
          </div>

          <div className="p-4 border border-gold/30 bg-gold/5 flex items-start gap-3" data-testid="price-gate">
            <Sparkles className="w-5 h-5 text-gold shrink-0 mt-0.5" />
            <p className="text-white/80 text-sm leading-relaxed">
              Presný rozpočet s kompletným rozpisom (materiál, práca, lešenie, doprava, DPH) vám
              <span className="text-gold"> pošleme na e-mail</span> po vyplnení kontaktu v kroku <span className="text-gold">04</span>.
            </p>
          </div>

          {/* materiál + partnerské ponuky (live náhľad) */}
          {live.our_part && (
            <div className="mt-6" data-testid="live-our-part">
              <div className="font-mono2 text-xs uppercase tracking-widest text-gold mb-2">Materiál a spotreba (Zateplenia.eu)</div>
              <div className="flex items-baseline justify-between border border-white/15 p-3">
                <span className="text-white/70 text-sm">vrátane dopravy a DPH</span>
                <span className="text-gold font-mono2 text-lg" data-testid="live-material-total">{eur(live.our_part.total_vat)}</span>
              </div>
            </div>
          )}
          {/* partnerské ponuky — pri mäkkej bráne rozmazané, kým zákazník nezadá e-mail (len ak je čo odomknúť) */}
          {(() => { const gated = gate && !captured && (live.partners?.length > 0 || !!live.partner?.district); return (
          <>
          <div className="relative">
            <div className={gated ? "blur-sm pointer-events-none select-none" : ""}>
          {live.partners?.length > 0 ? (
            <div className="mt-4" data-testid="live-partners">
              <div className="font-mono2 text-xs uppercase tracking-widest text-gold mb-2">Realizácia — firmy v okrese {live.partner?.district}</div>
              <div className="space-y-2">
                {live.partners.slice(0, 3).map((q, i) => (
                  <div key={i} className={`flex items-center justify-between gap-3 border p-3 ${q.featured ? "border-gold/60 bg-gold/5" : "border-white/15"}`} data-testid={`live-partner-${i}`}>
                    <span className="text-white text-sm font-medium flex items-center gap-2">
                      {q.name}
                      {q.featured && <span className="bg-gold text-ink text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5">Top partner</span>}
                    </span>
                    <span className="text-gold font-mono2 whitespace-nowrap text-sm">{eur(q.realization_total * (1 + (live.vat_pct || 23) / 100))} <span className="text-white/40 text-[10px]">s DPH</span></span>
                  </div>
                ))}
                {live.partners.length > 3 && (
                  <div className="text-white/40 text-xs">+ ďalšie {live.partners.length - 3} firmy — kompletný rozpis dostanete v ponuke</div>
                )}
              </div>
            </div>
          ) : live.partner?.district ? (
            <div className="mt-4 border border-white/15 p-4" data-testid="live-no-partner">
              <p className="text-white/70 text-sm">Pre okres <span className="text-gold">{live.partner.district}</span> zatiaľ nemáme zastúpenie realizačnou firmou.</p>
              <p className="text-white/45 text-xs mt-1">Orientačný odhad práce a lešenia: <span className="text-gold font-mono2">{eur((live.fallback_estimate?.total || 0) * (1 + (live.vat_pct || 23) / 100))} s DPH</span></p>
              <a href="/stat-sa-partnerom" className="inline-block mt-2 text-gold text-xs hover:underline">Poznáte spoľahlivú firmu? Odporučte nám ju →</a>
            </div>
          ) : null}
            </div>
            {gated && (
              <div className="absolute inset-0 flex items-center justify-center p-3" data-testid="price-soft-gate">
                <div className="w-full max-w-sm bg-ink border border-gold/50 p-4 shadow-2xl">
                  <p className="text-gold font-mono2 text-xs uppercase tracking-widest mb-1">Odomknite ponuky firiem z vášho okresu</p>
                  <p className="text-white/60 text-xs mb-3">+ detailný výkaz v PDF a bezplatná AI vizualizácia vašej fasády — stačí e-mail.</p>
                  <EmailCapture onCapture={onCapture} testid="calc-capture-gate" />
                </div>
              </div>
            )}
          </div>
          {!gated && (
            <div className="mt-4">
              {captured ? (
                <div className="border border-gold/30 bg-gold/5 p-3 flex items-center gap-2" data-testid="calc-capture-done-note">
                  <Check className="w-4 h-4 text-gold shrink-0" />
                  <p className="text-white/70 text-xs">{gate
                    ? "Ponuky odomknuté — kompletný rozpis vám letí do schránky."
                    : "Výpočet vám už letí do schránky — vrátane výkazu a linku na bezplatnú vizualizáciu fasády."}</p>
                </div>
              ) : (
                <EmailCapture onCapture={onCapture} testid="calc-capture-box" />
              )}
            </div>
          )}
          </> );})()}
          <div className="mt-3 flex items-center gap-2 text-white/45 text-xs font-mono2">
            <ShieldCheck className="w-4 h-4 text-gold" /> Záruka 36 mes. práca · 60 mes. materiál
          </div>
        </>
      )}
    </div>
  );
}

function FinalModal({ result, emailSent, contact, leadId, onClose, buildingType }) {
  const printRef = useRef(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [showAllPartners, setShowAllPartners] = useState(false);
  const [picked, setPicked] = useState(null);   // {id, name} — zákazníkom zvolená firma
  const [pickBusy, setPickBusy] = useState(false);

  const pickPartner = async (q) => {
    if (!leadId || pickBusy) return;
    setPickBusy(true);
    try {
      await api.post(`/leads/${leadId}/select-partner`, { partner_id: q.partner_id });
      setPicked({ id: q.partner_id, name: q.name });
      toast.success(`Vybrali ste ${q.name} — firme sme poslali notifikáciu.`);
      track("partner_selected", { partner: q.name });
    } catch {
      toast.error("Výber firmy sa nepodaril. Skúste znova.");
    } finally {
      setPickBusy(false);
    }
  };
  const email = contact?.email;

  const downloadPdf = async () => {
    if (!printRef.current) return;
    setPdfBusy(true);
    try {
      const canvas = await html2canvas(printRef.current, { scale: 2, backgroundColor: "#0A0A0A", useCORS: true });
      const pdf = new jsPDF({ unit: "pt", format: "a4" });
      const pw = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      // stránkovanie — obsah sa rozreže na A4 strany, nič sa neorezáva
      const scale = canvas.width / pw;
      const pagePxH = Math.floor(pageH * scale);
      const slice = document.createElement("canvas");
      let y = 0;
      while (y < canvas.height) {
        const h = Math.min(pagePxH, canvas.height - y);
        slice.width = canvas.width;
        slice.height = h;
        const ctx = slice.getContext("2d");
        ctx.fillStyle = "#0A0A0A";
        ctx.fillRect(0, 0, slice.width, h);
        ctx.drawImage(canvas, 0, y, canvas.width, h, 0, 0, canvas.width, h);
        if (y > 0) pdf.addPage();
        pdf.addImage(slice.toDataURL("image/jpeg", 0.95), "JPEG", 0, 0, pw, (h * pw) / canvas.width);
        y += h;
      }
      pdf.save("Zateplenia-cenova-ponuka.pdf");
      toast.success("PDF stiahnuté");
    } catch {
      toast.error("Nepodarilo sa vytvoriť PDF");
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] bg-ink/90 backdrop-blur-md flex items-start md:items-center justify-center overflow-y-auto p-4 md:p-8"
      data-testid="result-modal"
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-2xl bg-ink-2 border border-white/10 p-6 md:p-10 my-auto"
      >
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="w-12 h-12 bg-gold flex items-center justify-center mb-4"><Check className="w-7 h-7 text-ink" /></div>
            <h3 className="font-display text-4xl md:text-5xl text-white leading-none">Vaša cenová ponuka</h3>
          </div>
          <button onClick={onClose} data-testid="result-close" className="text-white/50 hover:text-white"><X className="w-6 h-6" /></button>
        </div>

        <div className="mb-8">
          <div className="text-white/40 text-xs font-mono2 uppercase tracking-widest mb-1">Materiál + realizácia — orientačne od (s DPH)</div>
          <div data-testid="final-total" className="font-display text-7xl md:text-8xl text-gold leading-none">{eur(result.total)}</div>
          <div className="text-white/50 mt-2 font-mono2 text-sm">
            {result.area} m² · hrúbka {result.thickness} mm · {result.material_name} · ≈ {eur(result.price_per_m2)}/m²
          </div>
        </div>

        <div className="border-t border-white/10">
          <Row label="Materiál (zateplovací systém)" value={eur(result.breakdown.material)} />
          {result.consumable_items && result.consumable_items.length > 0 ? (
            <details className="border-b border-white/10 group" data-testid="material-items">
              <summary className="cursor-pointer list-none flex items-center justify-between py-2.5 pl-4 text-xs text-white/40 hover:text-gold transition-colors">
                <span>Rozpis materiálu — presná spotrea a balenia (bez DPH)</span>
                <ChevronDown className="w-4 h-4 shrink-0 group-open:rotate-180 transition-transform" />
              </summary>
              <div className="pl-4">
                <SubRow label={`Izolačné dosky — ${result.material_name}, ${result.thickness} mm`} value={eur(result.material_items.boards)} />
                {result.consumable_items.map((ci) => (
                  <div key={ci.key} className="border-b border-white/5 last:border-0">
                    <SubRow label={ci.name} value={eur(ci.total_no_vat)} />
                    <div className="text-white/30 text-[11px] pb-2 -mt-1 pl-1" data-testid={`cons-qty-${ci.key}`}>
                      {ci.qty_text} → {ci.packs} {ci.unit} × {ci.unit_price_no_vat.toLocaleString("sk-SK")} €
                    </div>
                  </div>
                ))}
              </div>
            </details>
          ) : result.material_items && (
            <details className="border-b border-white/10 group" data-testid="material-items">
              <summary className="cursor-pointer list-none flex items-center justify-between py-2.5 pl-4 text-xs text-white/40 hover:text-gold transition-colors">
                <span>Rozpis materiálu — dosky, lepidlo, kotvy, sieťka, omietka</span>
                <ChevronDown className="w-4 h-4 shrink-0 group-open:rotate-180 transition-transform" />
              </summary>
              <div className="pl-4">
                <SubRow label={`Izolačné dosky — ${result.material_name}, ${result.thickness} mm`} value={eur(result.material_items.boards)} />
                <SubRow label="Lepiaca malta" value={eur(result.material_items.adhesive)} />
                <SubRow label="Kotvy (hmoždinky)" value={eur(result.material_items.anchors)} />
                <SubRow label="Armovacia vrstva (sieťka, stierka, náter)" value={eur(result.material_items.mesh_basecoat)} />
                <SubRow label="Penetrácia podkladu" value={eur(result.material_items.primer)} />
                <SubRow label="Fasádna omietka" value={eur(result.material_items.plaster)} />
              </div>
            </details>
          )}
          <Row label="Doprava" value={eur(result.breakdown.transport)} />
          {result.breakdown.profiles > 0 && <Row label={`Štartovacia lišta — ${result.precise?.starter_profile?.bm} bm`} value={eur(result.breakdown.profiles)} testid="row-profiles" />}
          {result.breakdown.sills > 0 && <Row label={`Parapety — ${result.precise?.sills?.bm} bm, hĺbka ${result.precise?.sills?.depth_mm} mm`} value={eur(result.breakdown.sills)} testid="row-sills" />}
          {result.breakdown.reveals > 0 && <Row label={`Špalety (zateplenie ostení) — ${result.precise?.reveals?.bm} bm`} value={eur(result.breakdown.reveals)} testid="row-reveals" />}
          {result.breakdown.corner_bead > 0 && <Row label={`Rohová lišta — ${result.precise?.corner_bead?.bm} bm`} value={eur(result.breakdown.corner_bead)} testid="row-corner" />}
          {result.breakdown.apu > 0 && <Row label={`APU lišta (okenná) — ${result.precise?.apu?.bm} bm`} value={eur(result.breakdown.apu)} testid="row-apu" />}
          {result.breakdown.drip > 0 && <Row label={`Okapová lišta — ${result.precise?.drip?.bm} bm`} value={eur(result.breakdown.drip)} testid="row-drip" />}
          {result.breakdown.soffit > 0 && <Row label={`Podhľad (${SOFFIT_LABELS[result.precise?.soffit?.variant] || "podhľad"}) — ${result.precise?.soffit?.area_m2} m²`} value={eur(result.breakdown.soffit)} testid="row-soffit" />}
          {result.breakdown.sokel > 0 && <Row label={`Sokel (XPS + lišta) — ${result.precise?.sokel?.area_m2} m²`} value={eur(result.breakdown.sokel)} testid="row-sokel" />}
          {result.breakdown.waste > 0 && <Row label={`Odpad / rezanie (${result.precise?.waste?.pct} % materiálu)`} value={eur(result.breakdown.waste)} testid="row-waste" />}
          {result.breakdown.anchoring > 0 && <Row label={`Kotvenie – ${result.wall_label || "murivo"}`} value={eur(result.breakdown.anchoring)} />}
          {result.breakdown.urgency > 0 && <Row label="Expresná realizácia" value={eur(result.breakdown.urgency)} />}
          {result.our_part && (
            <div className="flex items-baseline justify-between gap-3 py-2.5 mt-1 border-t border-gold/30" data-testid="row-our-part">
              <span className="text-gold font-semibold text-sm">Materiál a spotreba spolu s DPH</span>
              <span className="font-mono2 text-gold font-bold whitespace-nowrap">{eur(result.our_part.total_vat)}</span>
            </div>
          )}
        </div>

        {/* REALIZÁCIA — partnerské ponuky práce + lešenia */}
        <div className="mt-6 border border-white/10 p-5" data-testid="realization-section">
          <div className="font-mono2 text-xs uppercase tracking-widest text-gold mb-3">Realizácia (práca + lešenie)</div>
          {result.partners?.length > 0 ? (
            <div className="space-y-3">
              {(showAllPartners ? result.partners : result.partners.slice(0, 3)).map((q, i) => (
                <div key={i} className={`border p-4 ${q.featured ? "border-gold/60 bg-gold/5" : "border-white/15"}`} data-testid={`partner-quote-${i}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <span className="text-white font-semibold text-sm flex items-center gap-2 flex-wrap">
                      {q.name}
                      {q.featured && <span className="bg-gold text-ink text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5">Top partner</span>}
                    </span>
                    <span className="text-gold font-mono2 font-bold whitespace-nowrap">{eur(q.realization_total * (1 + (result.vat_pct || 23) / 100))} <span className="text-white/40 text-[10px] font-normal">s DPH</span></span>
                  </div>
                  <div className="text-white/45 text-xs mt-1.5">
                    Práca {eur(q.labor_total)} ({q.labor_rate_per_m2} €/m²) · Lešenie {eur(q.scaffold.total)}
                    {(q.scaffold.delivery > 0 || q.scaffold.dismantle > 0 || q.scaffold.rent > 0) && (
                      <span className="text-white/30"> (dovoz/odvoz {eur(q.scaffold.delivery)}, montáž {eur(q.scaffold.setup)}, prenájom {eur(q.scaffold.rent)}, demontáž {eur(q.scaffold.dismantle)})</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
                    {q.rating ? (
                      <span className="flex items-center gap-1.5 text-xs text-gold" data-testid={`partner-rating-${i}`}>
                        <Star className="w-4 h-4 fill-gold" /> {q.rating} <span className="text-white/40">({q.reviews_count} hodnotení)</span>
                      </span>
                    ) : (
                      <span className="text-white/30 text-xs">Zatiaľ bez hodnotení</span>
                    )}
                    {leadId && (
                      picked?.id === q.partner_id ? (
                        <span className="flex items-center gap-2 text-gold text-xs font-bold uppercase tracking-wider" data-testid={`partner-picked-${i}`}>
                          <Check className="w-4 h-4" /> Vaša voľba — firma vás bude kontaktovať
                        </span>
                      ) : (
                        <button onClick={() => pickPartner(q)} disabled={pickBusy} data-testid={`partner-pick-${i}`}
                          className={`text-xs font-bold uppercase tracking-wider px-4 py-2 transition-colors ${picked ? "border border-white/20 text-white/50 hover:border-gold hover:text-gold" : "bg-gold text-ink hover:bg-gold-light"}`}>
                          {pickBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (picked ? "Zmeniť na túto firmu" : "Chcem túto firmu")}
                        </button>
                      )
                    )}
                  </div>
                </div>
              ))}
              {result.partners.length > 3 && (
                <button onClick={() => setShowAllPartners((v) => !v)} data-testid="partners-toggle"
                  className="w-full text-center text-gold text-xs py-2 border border-gold/30 hover:bg-gold/10 transition-colors">
                  {showAllPartners ? "Skryť ďalšie firmy" : `Zobraziť všetkých ${result.partners.length} partnerov v okrese`}
                </button>
              )}
              <p className="text-white/35 text-[11px]">Ceny firiem sú orientačné — finálnu cenu potvrdí vybraná firma po bezplatnej obhliadke.</p>
            </div>
          ) : (
            <div data-testid="no-partner-cta">
              {result.fallback_estimate && (
                <div className="flex items-baseline justify-between gap-3 border border-white/15 p-4 mb-3">
                  <span className="text-white/70 text-sm">Orientačný odhad práce a lešenia<br /><span className="text-white/35 text-[11px]">(náš cenník — presnú cenu potvrdí firma po obhliadke)</span></span>
                  <span className="font-mono2 text-gold whitespace-nowrap">{eur(result.fallback_estimate.total * (1 + (result.vat_pct || 23) / 100))} <span className="text-white/40 text-[10px]">s DPH</span></span>
                </div>
              )}
              <div className="border border-dashed border-gold/40 bg-gold/5 p-4">
                <p className="text-white/80 text-sm">
                  Pre okres <span className="text-gold font-semibold">{result.partner?.district || "—"}</span> zatiaľ nemáme zastúpenie realizačnou firmou.
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <a href="/stat-sa-partnerom" data-testid="cta-become-partner" className="text-xs font-bold uppercase tracking-wider bg-gold text-ink px-4 py-2.5 hover:bg-gold-light transition-colors">Stať sa partnerom</a>
                  <a href="/stat-sa-partnerom" data-testid="cta-recommend-partner" className="text-xs font-bold uppercase tracking-wider border border-gold/50 text-gold px-4 py-2.5 hover:bg-gold/10 transition-colors">Odporučiť spoľahlivú firmu</a>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-white/10">
          <Row label="Medzisúčet bez DPH (materiál + realizácia)" value={eur(result.subtotal)} />
          <Row label={`DPH ${result.vat_pct}%`} value={eur(result.vat)} />
        </div>

        {result.precise && (
          <details className="mt-4 border border-gold/25 bg-gold/[0.03] group" data-testid="vykaz-vymery">
            <summary className="cursor-pointer list-none flex items-center justify-between px-5 py-3 text-xs font-mono2 uppercase tracking-widest text-gold hover:text-gold-light transition-colors">
              <span>Výkaz výmery — presný režim</span>
              <ChevronDown className="w-4 h-4 shrink-0 group-open:rotate-180 transition-transform" />
            </summary>
            <div className="px-5 pb-4">
              <SubRow label="Obvod domu (štartovacia + okapová lišta)" value={`${result.precise.dimensions.perimeter_m} bm`} />
              <SubRow label={`Výška fasády (${result.precise.dimensions.floors} podl.)`} value={`${result.precise.dimensions.height_m} m`} />
              {result.precise.dimensions.gross_area_m2 > 0 && (
                <SubRow label="Plocha fasády (hrubá)" value={`${result.precise.dimensions.gross_area_m2} m²`} />
              )}
              {result.precise.openings?.known && (
                <SubRow label={`Okná a dvere (${result.precise.openings.count} ks)`} value={`− ${result.precise.openings.area_m2} m²`} />
              )}
              {result.precise.net_area_m2 > 0 && (
                <SubRow label="Čisté zateplenie (fasáda − otvory)" value={`${result.precise.net_area_m2} m²`} />
              )}
              <SubRow label="Lešenie (obvod + nárožia) × výška" value={`${result.precise.dimensions.scaffold_area_m2} m²`} />
              <SubRow label={`Parapety — hĺbka ${result.precise.sills.depth_mm} mm (izolácia ${result.thickness} + 60 mm)`} value={`${result.precise.sills.bm} bm`} />
              {(result.precise.sills.windows || []).filter((w) => !w.full_height).map((w, i) => (
                <SubRow key={i} label={`↳ ${w.kind === "door" ? "dvere" : "okno"} ${w.width_m} × ${w.height_m} m × ${w.count} ks`} value={`${w.sill_bm} bm`} />
              ))}
              {(result.precise.sills.windows || []).some((w) => w.full_height) && (
                <SubRow label="↳ okná od zeme a dvere — bez parapetu" value="0 bm" />
              )}
              {result.precise.reveals?.bm > 0 && (
                <SubRow label="Obvod otvorov → špalety (ostenie)" value={`${result.precise.reveals.bm} bm`} />
              )}
              {result.precise.corner_bead?.bm > 0 && (
                <SubRow label={`Rohová lišta (${result.precise.corner_bead.corners} rohy domu + otvory, +10 % rezerva)`} value={`${result.precise.corner_bead.bm} bm`} />
              )}
              {result.precise.apu?.bm > 0 && (
                <SubRow label="APU lišta (obvod otvorov + rezerva)" value={`${result.precise.apu.bm} bm`} />
              )}
              {result.precise.drip?.bm > 0 && (
                <SubRow label="Okapová lišta (zakladanie + horné hrany + podhľad)" value={`${result.precise.drip.bm} bm`} />
              )}
              {result.precise.sokel.area_m2 > 0 && (
                <SubRow label={`Sokel — obvod × ${result.precise.sokel.height_m} m`} value={`${result.precise.sokel.area_m2} m²`} />
              )}
              {result.precise.soffit && (
                <>
                  <SubRow label={`Podhľad — obvod × (${result.precise.soffit.width_m} m + čielko ${result.precise.soffit.fascia_m} m)`} value={`${result.precise.soffit.area_m2} m²`} />
                  <div className="mt-2 pt-2 border-t border-white/10" data-testid="soffit-compare">
                    <div className="text-white/40 text-[11px] mb-1.5">Porovnanie variantov podhľadu (rovnaká plocha):</div>
                    {Object.entries(result.precise.soffit.variants || {}).map(([k, v]) => (
                      <SubRow key={k} label={`↳ ${SOFFIT_LABELS[k] || k}${k === result.precise.soffit.variant ? " — zvolené" : ""}`} value={eur(v)} />
                    ))}
                  </div>
                </>
              )}
              <SubRow label="Odpad / rezanie" value={`${result.precise.waste.pct} %`} />
            </div>
          </details>
        )}

        <MarketCompare result={result} />

        <SavingsEstimate area={result.area} />

        {result.partner?.has_partner && (
          <div className="mt-4 p-3 border border-gold/30 bg-gold/5 flex items-center gap-2 text-gold text-sm" data-testid="final-partner">
            <ShieldCheck className="w-5 h-5 shrink-0" />
            Realizuje overený partner: {result.partner.name} ({result.partner.district})
          </div>
        )}

        <div className="mt-3 grid grid-cols-2 gap-3">
          <div className="p-3 border border-white/10 text-center">
            <div className="font-display text-3xl text-gold">36</div>
            <div className="text-white/50 text-xs uppercase tracking-widest font-mono2">mesiacov záruka práca</div>
          </div>
          <div className="p-3 border border-white/10 text-center">
            <div className="font-display text-3xl text-gold">60</div>
            <div className="text-white/50 text-xs uppercase tracking-widest font-mono2">mesiacov záruka materiál</div>
          </div>
        </div>

        <div className="mt-6 p-4 border border-gold/30 bg-gold/5 flex items-center gap-3">
          <Check className="w-5 h-5 text-gold shrink-0" />
          <p className="text-white/80 text-sm">
            {emailSent
              ? <>Ponuku sme odoslali na <span className="text-gold">{email}</span>. Čoskoro vás kontaktujeme.</>
              : <>Ponuka je pripravená. Čoskoro vás kontaktujeme na <span className="text-gold">{email}</span>.</>}
          </p>
        </div>

        <Link
          to={`/rezervacia?lead=${encodeURIComponent(leadId || "")}&name=${encodeURIComponent(contact?.name || "")}&email=${encodeURIComponent(contact?.email || "")}`}
          data-testid="book-inspection-cta"
          className="mt-6 w-full bg-gold text-ink py-4 font-bold uppercase tracking-wider hover:bg-gold-light transition-colors flex items-center justify-center gap-2"
        >
          <CalendarDays className="w-5 h-5" /> Rezervovať bezplatnú obhliadku
        </Link>

        <Link
          to={`/objednavka?name=${encodeURIComponent(contact?.name || "")}&email=${encodeURIComponent(contact?.email || "")}&area=${encodeURIComponent(result.area || "")}&material=${encodeURIComponent(result.material_name || "")}&thickness=${encodeURIComponent(result.thickness || "")}&type=${encodeURIComponent(buildingType || "")}&price=${encodeURIComponent(Math.round(result.total) || "")}&ppm=${encodeURIComponent(Math.round(result.price_per_m2) || "")}`}
          data-testid="create-order-cta"
          className="mt-3 w-full border border-gold/50 text-gold py-4 font-bold uppercase tracking-wider hover:bg-gold hover:text-ink transition-colors flex items-center justify-center gap-2"
        >
          <ClipboardList className="w-5 h-5" /> Vytvoriť objednávku
        </Link>

        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button onClick={downloadPdf} disabled={pdfBusy} data-testid="download-pdf"
            className="flex items-center justify-center gap-2 border border-gold text-gold py-4 font-bold uppercase tracking-wider hover:bg-gold hover:text-ink transition-colors">
            {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Stiahnuť PDF
          </button>
          <button onClick={onClose} className="border border-white/20 text-white/70 py-4 font-bold uppercase tracking-wider hover:border-white/40 transition-colors" data-testid="result-done">
            Zavrieť
          </button>
        </div>
      </motion.div>

      {/* hidden printable quote for PDF */}
      <div style={{ position: "fixed", top: 0, left: "-10000px", width: "800px" }}>
        <div ref={printRef} style={{ width: "800px", background: "#0A0A0A", color: "#fff", fontFamily: "Manrope, Arial, sans-serif", padding: "48px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "2px solid #D4AF37", paddingBottom: "20px" }}>
            <div>
              <div style={{ fontFamily: "'Anton', sans-serif", fontSize: "34px", color: "#D4AF37", letterSpacing: "2px" }}>ZATEPLENIA.EU</div>
              <div style={{ color: "#A3A3A3", fontSize: "13px", marginTop: "4px" }}>Online kalkulačka zateplenia · Slovensko</div>
            </div>
            <div style={{ textAlign: "right", color: "#A3A3A3", fontSize: "12px" }}>
              Cenová ponuka<br />{new Date().toLocaleDateString("sk-SK")}
            </div>
          </div>

          <div style={{ marginTop: "28px" }}>
            <div style={{ color: "#A3A3A3", fontSize: "12px", textTransform: "uppercase", letterSpacing: "2px" }}>Materiál + realizácia — orientačne od (s DPH)</div>
            <div style={{ fontFamily: "'Anton', sans-serif", fontSize: "72px", color: "#D4AF37", lineHeight: 1 }}>{eur(result.total)}</div>
            <div style={{ color: "#A3A3A3", fontSize: "14px", marginTop: "6px" }}>
              {contact?.name ? `Pre: ${contact.name} · ` : ""}{result.area} m² · hrúbka {result.thickness} mm · {result.material_name}
            </div>
          </div>

          <table style={{ width: "100%", marginTop: "28px", borderCollapse: "collapse", fontSize: "15px" }}>
            <tbody>
              {[
                ["Materiál (zateplovací systém)", result.breakdown.material],
                ["Doprava", result.breakdown.transport],
                ...(result.breakdown.profiles > 0 ? [[`Štartovacia lišta — ${result.precise?.starter_profile?.bm} bm`, result.breakdown.profiles]] : []),
                ...(result.breakdown.sills > 0 ? [[`Parapety — ${result.precise?.sills?.bm} bm, hĺbka ${result.precise?.sills?.depth_mm} mm`, result.breakdown.sills]] : []),
                ...(result.breakdown.reveals > 0 ? [[`Špalety (ostenie) — ${result.precise?.reveals?.bm} bm`, result.breakdown.reveals]] : []),
                ...(result.breakdown.corner_bead > 0 ? [[`Rohová lišta — ${result.precise?.corner_bead?.bm} bm`, result.breakdown.corner_bead]] : []),
                ...(result.breakdown.apu > 0 ? [[`APU lišta — ${result.precise?.apu?.bm} bm`, result.breakdown.apu]] : []),
                ...(result.breakdown.drip > 0 ? [[`Okapová lišta — ${result.precise?.drip?.bm} bm`, result.breakdown.drip]] : []),
                ...(result.breakdown.soffit > 0 ? [[`Podhľad (${SOFFIT_LABELS[result.precise?.soffit?.variant] || ""}) — ${result.precise?.soffit?.area_m2} m²`, result.breakdown.soffit]] : []),
                ...(result.breakdown.sokel > 0 ? [[`Sokel (XPS + lišta) — ${result.precise?.sokel?.area_m2} m²`, result.breakdown.sokel]] : []),
                ...(result.breakdown.waste > 0 ? [[`Odpad / rezanie (${result.precise?.waste?.pct} %)`, result.breakdown.waste]] : []),
                ...(result.breakdown.anchoring > 0 ? [[`Kotvenie – ${result.wall_label || "murivo"}`, result.breakdown.anchoring]] : []),
                ...(result.breakdown.urgency > 0 ? [["Expresná realizácia", result.breakdown.urgency]] : []),
                ["— MATERIÁL A SPOTREBA SPOLU S DPH —", result.our_part?.total_vat ?? result.breakdown.material],
                ...(result.partners?.length
                  ? result.partners.map((q) => [`Realizácia: ${q.name}${q.featured ? " (Top partner)" : ""} — práca + lešenie (bez DPH)`, q.realization_total])
                  : [[`Realizácia — orientačný odhad práce a lešenia (s DPH)`, (result.fallback_estimate?.total ?? result.breakdown.labor + result.breakdown.scaffolding) * (1 + (result.vat_pct || 23) / 100)]]),
                ["Medzisúčet bez DPH", result.subtotal],
                [`DPH ${result.vat_pct}%`, result.vat],
              ].map(([l, v]) => (
                <tr key={l}>
                  <td style={{ padding: "10px 0", color: "#ccc", borderBottom: "1px solid #222" }}>{l}</td>
                  <td style={{ padding: "10px 0", textAlign: "right", color: "#fff", borderBottom: "1px solid #222", fontFamily: "'Space Mono', monospace" }}>{eur(v)}</td>
                </tr>
              ))}
              <tr>
                <td style={{ padding: "16px 0", fontFamily: "'Anton', sans-serif", fontSize: "26px", color: "#fff" }}>MATERIÁL + REALIZÁCIA OD</td>
                <td style={{ padding: "16px 0", textAlign: "right", fontFamily: "'Anton', sans-serif", fontSize: "26px", color: "#D4AF37" }}>{eur(result.total)}</td>
              </tr>
            </tbody>
          </table>

          {result.partners?.length > 0 && (
            <div style={{ marginTop: "12px", padding: "12px", border: "1px solid rgba(212,175,55,0.4)", color: "#D4AF37", fontSize: "13px" }}>
              Realizáciu ponúkajú overení partneri v okrese {result.partner?.district}: {result.partners.map((q) => q.name).join(", ")}. Finálnu cenu potvrdí vybraná firma po obhliadke.
            </div>
          )}

          {result.precise && (
            <div style={{ marginTop: "24px", border: "1px solid rgba(212,175,55,0.35)", padding: "18px" }} data-testid="pdf-vykaz">
              <div style={{ color: "#D4AF37", fontSize: "12px", textTransform: "uppercase", letterSpacing: "2px", marginBottom: "10px" }}>Výkaz výmery — presný režim</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <tbody>
                  {[
                    ["Obvod domu (štartovacia + okapová lišta)", `${result.precise.dimensions.perimeter_m} bm`],
                    [`Výška fasády (${result.precise.dimensions.floors} podl.)`, `${result.precise.dimensions.height_m} m`],
                    ...(result.precise.dimensions.gross_area_m2 > 0 ? [["Plocha fasády (hrubá)", `${result.precise.dimensions.gross_area_m2} m²`]] : []),
                    ...(result.precise.openings?.known ? [[`Okná a dvere (${result.precise.openings.count} ks)`, `− ${result.precise.openings.area_m2} m²`]] : []),
                    ...(result.precise.net_area_m2 > 0 ? [["Čisté zateplenie (fasáda − otvory)", `${result.precise.net_area_m2} m²`]] : []),
                    ["Lešenie (obvod + nárožia) × výška", `${result.precise.dimensions.scaffold_area_m2} m²`],
                    [`Parapety — hĺbka ${result.precise.sills.depth_mm} mm (izolácia + 60 mm)`, `${result.precise.sills.bm} bm`],
                    ...(result.precise.sills.windows || []).filter((w) => !w.full_height).map((w) => [`↳ ${w.kind === "door" ? "dvere" : "okno"} ${w.width_m} × ${w.height_m} m × ${w.count} ks`, `${w.sill_bm} bm`]),
                    ...((result.precise.sills.windows || []).some((w) => w.full_height) ? [["↳ okná od zeme a dvere — bez parapetu", "0 bm"]] : []),
                    ...(result.precise.reveals?.bm > 0 ? [["Špalety (obvod otvorov → ostenie)", `${result.precise.reveals.bm} bm`]] : []),
                    ...(result.precise.corner_bead?.bm > 0 ? [[`Rohová lišta (${result.precise.corner_bead.corners} rohy domu + otvory, +10 % rezerva)`, `${result.precise.corner_bead.bm} bm`]] : []),
                    ...(result.precise.apu?.bm > 0 ? [["APU lišta (obvod otvorov + rezerva)", `${result.precise.apu.bm} bm`]] : []),
                    ...(result.precise.drip?.bm > 0 ? [["Okapová lišta (zakladanie + horné hrany + podhľad)", `${result.precise.drip.bm} bm`]] : []),
                    ...(result.precise.sokel?.area_m2 > 0 ? [[`Sokel — obvod × ${result.precise.sokel.height_m} m`, `${result.precise.sokel.area_m2} m²`]] : []),
                    ...(result.precise.soffit ? [[`Podhľad — ${SOFFIT_LABELS[result.precise.soffit.variant] || "podhľad"}`, `${result.precise.soffit.area_m2} m²`]] : []),
                    ["Odpad / rezanie materiálu", `${result.precise.waste.pct} %`],
                  ].map(([l, v]) => (
                    <tr key={l}>
                      <td style={{ padding: "6px 0", color: "#bbb", borderBottom: "1px solid #1c1c1c" }}>{l}</td>
                      <td style={{ padding: "6px 0", textAlign: "right", color: "#fff", borderBottom: "1px solid #1c1c1c", fontFamily: "'Space Mono', monospace", whiteSpace: "nowrap" }}>{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {result.precise.soffit && (
                <div style={{ marginTop: "10px", fontSize: "12px", color: "#999" }}>
                  Porovnanie variantov podhľadu (rovnaká plocha): {Object.entries(result.precise.soffit.variants || {}).map(([k, v]) => `${SOFFIT_LABELS[k] || k} ${eur(v)}`).join(" · ")}
                </div>
              )}
              <div style={{ marginTop: "10px", fontSize: "11px", color: "#666" }}>
                Množstvá sú orientačné podľa vášho zadania — presné množstvo potvrdí bezplatná obhliadka.
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: "12px", marginTop: "20px" }}>
            <div style={{ flex: 1, border: "1px solid #222", padding: "14px", textAlign: "center" }}>
              <div style={{ fontFamily: "'Anton', sans-serif", fontSize: "30px", color: "#D4AF37" }}>36 mesiacov</div>
              <div style={{ color: "#888", fontSize: "11px", textTransform: "uppercase", letterSpacing: "1px" }}>záruka na prácu</div>
            </div>
            <div style={{ flex: 1, border: "1px solid #222", padding: "14px", textAlign: "center" }}>
              <div style={{ fontFamily: "'Anton', sans-serif", fontSize: "30px", color: "#D4AF37" }}>60 mesiacov</div>
              <div style={{ color: "#888", fontSize: "11px", textTransform: "uppercase", letterSpacing: "1px" }}>záruka na materiál</div>
            </div>
          </div>

          <div style={{ marginTop: "24px", color: "#666", fontSize: "11px", borderTop: "1px solid #222", paddingTop: "14px" }}>
            Ide o orientačný odhad. Presnú cenu potvrdíme po obhliadke alebo prehliadke projektu. Zateplenia.eu · info@zateplenia.eu
          </div>
        </div>
      </div>
    </motion.div>
  );
}
